import { Color, PaneAmount, PaneItemChild, PaymentPaneProps, Text } from '@billy/design-system'
import { TFunction } from 'i18next'
import React from 'react'

import { ConnectBankBanner } from '@features/bankAccounts/elements/ConnectBankBanner'
import type { ConnectBankBannerState } from '@features/bankAccounts/hooks/useConnectBankBanner'
import { InvoiceStatus } from '@features/invoicesLegacy/enums/invoiceStatus'
import { Invoice } from '@features/invoicesLegacy/types/invoice'
import { getInvoiceStatus } from '@features/invoicesLegacy/utils/getInvoiceStatus'
import { UiSource } from '@features/reconciliation/enums'

import { invoiceStatusToPaymentPaneStatus } from '../constants/invoiceStatusToPaymentPaneStatus'
import { InvoicePaymentPaneExtra } from '../elements/InvoicePaymentPaneExtra'
import { InvoicePaymentPanePrefix } from '../elements/InvoicePaymentPanePrefix'
import { getModifiersCount } from './getModifiersCount'

export const getInvoicePaymentPaneData = (
  t: TFunction,
  invoiceData?: Invoice,
  isLoading?: boolean,
  invoiceStatusOverride?: InvoiceStatus,
  connectBankBanner?: ConnectBankBannerState,
): PaymentPaneProps => {
  if (!invoiceData || isLoading) {
    return { isLoading }
  }

  const { amount, balance, balanceModifiers, dueDate, entryDate, isPaid, referredCreditnoteId, state, tax } =
    invoiceData

  const invoiceStatus =
    invoiceStatusOverride ||
    getInvoiceStatus({
      amount,
      balance,
      dueDate: dueDate ? new Date(dueDate) : undefined,
      isPaid,
      referredCreditnoteId,
      state,
      tax,
    })

  const { payments, fees } = getModifiersCount(balanceModifiers)

  const modifiersCount = balanceModifiers?.length
  const isPaidOrDraftBillStatus = invoiceStatus === InvoiceStatus.Paid || invoiceStatus === InvoiceStatus.Draft
  const paidDate = modifiersCount ? balanceModifiers[0].entryDate : entryDate
  const isUnpaidOrOverdue = invoiceStatus === InvoiceStatus.Overdue || invoiceStatus === InvoiceStatus.Unpaid
  const amountColor = isUnpaidOrOverdue ? Color.Red700 : Color.Grey200

  return {
    amount: (
      <PaneItemChild>
        <PaneAmount amount={balance} color={amountColor} />
      </PaneItemChild>
    ),
    banner: isUnpaidOrOverdue && connectBankBanner?.isVisible && (
      <ConnectBankBanner uiSource={UiSource.InvoiceScreen} variant="invoice" onDismiss={connectBankBanner.onDismiss} />
    ),
    collapsible: !!modifiersCount,
    content: !!modifiersCount && (
      <Text variant="bodySM" inline>
        {!!payments && t('invoices.view.payment_pane.content.payment', { count: payments })}
        {!!payments && !!fees && ` ${t('invoices.view.payment_pane.content.and')} `}
        {!!fees && t('invoices.view.payment_pane.content.fee', { count: fees })}
      </Text>
    ),
    extra: (
      <PaneItemChild>
        <InvoicePaymentPaneExtra date={isPaidOrDraftBillStatus ? paidDate : dueDate} status={invoiceStatus} />
      </PaneItemChild>
    ),
    icon: 'circleAlert',
    prefix: <InvoicePaymentPanePrefix status={invoiceStatus} />,
    status: invoiceStatusToPaymentPaneStatus[invoiceStatus],
  }
}
